/**
 * monzo-derived-routes — the SCHEMA-FREE, behaviour-free ROUTE-ONLY half of the
 * Monzo derived tail (#904, the #705 route-only precedent extended to the
 * hand-authored DERIVED plugins).
 *
 * The FULL `monzoDerivedPlugin` (`./monzo-derived-plugin.js`) composes its
 * behavioural entities (`store` + two `query`s + an `event` + two `action`s + an
 * `effect` FSM) ALONGSIDE its `component`/`route` pair. But the studio ENTRY's
 * router (gate-4, boot, navigation) needs only the ROUTE TABLE + each route's
 * (lazy) editor — it never looks up a `store`/`query`/`effect` entity. Composing
 * the full plugin on the eager entry therefore drags those behavioural entity
 * literals onto the entry chunk for no boot benefit; this module carries the
 * route + component entities ONLY, so the eager `genome.ts` composes THIS while
 * `genome-full.ts` keeps the FULL plugin (whose full 16-kind graph the off-entry
 * agent catalog / PERCEIVE / genome-graph / census consume).
 *
 * The lazy editor consts + the `component`/`route` entity literals + the plugin
 * id live HERE (the entry-safe home); `monzo-derived-plugin.ts` IMPORTS
 * {@link MONZO_DERIVED_ROUTE_ENTITIES} + {@link MONZO_DERIVED_EDITORS} + the id
 * and re-exports the id, then prepends its behavioural block — so
 * `MONZO_DERIVED_ENTITIES` stays ELEMENT-IDENTICAL to before (the behavioural
 * block was already the leading contiguous slice, the route block the trailing
 * one). Nothing here reaches the FULL plugin, so `genome.ts` → this module never
 * pulls the behavioural entities onto the entry.
 *
 * @module @ubiclouds/v5-studio/monzo/monzo-derived-routes
 */

import { lazy, type ComponentType } from 'react';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { EntityDef } from '@ubiclouds/v5-sdk/genome';

/** The bespoke derived-tail plugin id (its entity namespace). */
export const MONZO_DERIVED_PLUGIN_ID = 'monzo-derived';

/** The split-chunk insights editor (formatMoney + the CSS/SVG spend bar stay off the entry). */
const LazyMonzoInsights: ComponentType = lazy(() => import('./MonzoInsights.js'));

/** The split-chunk budget-planner editor (kept OFF the entry, gate 8). */
const LazyMonzoBudget: ComponentType = lazy(() => import('./MonzoBudget.js'));

/**
 * The `component`/`route` entities the derived tail contributes (keyed by
 * `meta.id`). Element-identical to the trailing route block of the historic
 * `MONZO_DERIVED_ENTITIES` — the FULL plugin re-uses this array verbatim.
 */
export const MONZO_DERIVED_ROUTE_ENTITIES: readonly EntityDef[] = [
  { kind: 'component', meta: { id: `${MONZO_DERIVED_PLUGIN_ID}:component/monzo-insights` } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: `${MONZO_DERIVED_PLUGIN_ID}:component/monzo-insights` }],
    meta: { id: `${MONZO_DERIVED_PLUGIN_ID}:route/insights`, path: '/monzo/insights', title: 'Monzo Insights' },
  },
  // The BUDGET PLANNER — set monthly per-category limits + track actual spend from
  // the ledger. Not a CRUD quartet (a component + route over the budget store), so
  // the compiled-not-authored gate stays green.
  { kind: 'component', meta: { id: `${MONZO_DERIVED_PLUGIN_ID}:component/monzo-budget` } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: `${MONZO_DERIVED_PLUGIN_ID}:component/monzo-budget` }],
    meta: { id: `${MONZO_DERIVED_PLUGIN_ID}:route/budget`, path: '/monzo/budget', title: 'Budget Planner' },
  },
];

/** The lazy editor map — SHARED by the route-only plugin and the FULL plugin. */
export const MONZO_DERIVED_EDITORS: Readonly<Record<string, ComponentType>> = Object.freeze({
  'monzo-insights': LazyMonzoInsights,
  'monzo-budget': LazyMonzoBudget,
});

/**
 * The ROUTE-ONLY Monzo derived-tail plugin (composed on the studio ENTRY by
 * `genome.ts`). Same `editors` as the FULL plugin; `entities()` are the
 * `component`/`route` pair ONLY — the behavioural entities live in the FULL
 * plugin, composed OFF the entry by `genome-full.ts`.
 */
export const monzoDerivedRoutePlugin: ComposableGenomePlugin = Object.freeze({
  id: MONZO_DERIVED_PLUGIN_ID,
  name: 'Monzo Insights',
  version: '1.0.0',
  trustTier: 'core' as const,
  editors: MONZO_DERIVED_EDITORS,
  entities(): EntityDef[] {
    return [...MONZO_DERIVED_ROUTE_ENTITIES];
  },
});
